import React from 'react';
import dayjs from 'dayjs';
import { openEngineCommercialPrint } from '@/components/print/openEngineCommercialPrint';
import type { IcSettlement, IcTransaction } from '@/services/intercompany.service';

type FormatCurrency = (amount: number, currency?: string) => string;

export function printIcTransaction(
  tx: IcTransaction,
  opts: {
    formatCurrency: FormatCurrency;
    sellerName?: string;
    buyerName?: string;
  }
) {
  const seller = opts.sellerName || tx.seller_company_id.slice(0, 8);
  const buyer = opts.buyerName || tx.buyer_company_id.slice(0, 8);
  const lines = (tx.lines ?? []).map((l, i) => ({
    lineNumber: l.line_number ?? i + 1,
    description: l.item_description || l.item_code || '—',
    cells: {
      code: l.item_code || '',
      qty: String(l.quantity ?? ''),
      uom: l.uom || '',
      price: opts.formatCurrency(Number(l.unit_price), tx.currency),
      total: opts.formatCurrency(Number(l.line_total), tx.currency),
    },
  }));

  openEngineCommercialPrint({
    docType: 'intercompany_transaction',
    title: 'INTERCOMPANY TRANSACTION',
    documentNumber: tx.ic_number,
    partyName: buyer,
    letterhead: true,
    metaLines: [
      { label: 'IC No', value: tx.ic_number },
      { label: 'Date', value: tx.transaction_date ? dayjs(tx.transaction_date).format('DD MMM YYYY') : '—' },
      { label: 'Type', value: String(tx.transaction_type || '') },
      { label: 'Status', value: tx.status },
      { label: 'Seller', value: seller },
      { label: 'Buyer', value: buyer },
      { label: 'Currency', value: tx.currency || 'AED' },
    ],
    introText: tx.notes || undefined,
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'description', label: 'Description' },
      { key: 'qty', label: 'Qty', align: 'right' },
      { key: 'uom', label: 'UOM' },
      { key: 'price', label: 'Unit price', align: 'right' },
      { key: 'total', label: 'Line total', align: 'right' },
    ],
    lines,
    finals: [{ id: 'totals', heightPx: 72, keepTogetherWithNext: false }],
    finalBlocks: {
      totals: (
        <div style={{ textAlign: 'right', fontSize: 12, lineHeight: 1.6 }}>
          <div>Subtotal: {opts.formatCurrency(Number(tx.subtotal), tx.currency)}</div>
          <div>VAT: {opts.formatCurrency(Number(tx.vat_amount), tx.currency)}</div>
          <div>
            <strong>Grand total: {opts.formatCurrency(Number(tx.grand_total), tx.currency)}</strong>
          </div>
        </div>
      ),
    },
  });
}

export function printIcSettlement(
  row: IcSettlement,
  opts: { formatCurrency: FormatCurrency; counterpartyName?: string }
) {
  openEngineCommercialPrint({
    docType: 'intercompany_settlement',
    title: 'INTERCOMPANY SETTLEMENT',
    documentNumber: row.settlement_number,
    partyName: opts.counterpartyName || row.counterparty_company_id.slice(0, 8),
    letterhead: true,
    metaLines: [
      { label: 'Settlement No', value: row.settlement_number },
      {
        label: 'Date',
        value: row.settlement_date ? dayjs(row.settlement_date).format('DD MMM YYYY') : '—',
      },
      { label: 'Type', value: row.settlement_type },
      { label: 'Status', value: row.status },
      { label: 'Counterparty', value: opts.counterpartyName || row.counterparty_company_id },
      {
        label: 'Due from (before)',
        value: opts.formatCurrency(Number(row.due_from_before), row.currency),
      },
      {
        label: 'Due to (before)',
        value: opts.formatCurrency(Number(row.due_to_before), row.currency),
      },
      {
        label: 'Settled amount',
        value: opts.formatCurrency(Number(row.netted_amount), row.currency),
      },
    ],
    introText: row.notes || undefined,
    columns: [
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount', align: 'right' },
    ],
    lines: [
      {
        lineNumber: 1,
        description: `${row.settlement_type} settlement`,
        cells: {
          amount: opts.formatCurrency(Number(row.netted_amount), row.currency),
        },
      },
    ],
  });
}

